import { useEffect, useState } from "react";
import { toast } from "sonner";
import { LayoutDashboard, Building2, Users } from "lucide-react";
import Shell from "@/components/Shell";
import { Panel, Btn, Input, StatusBadge } from "@/components/kit";
import api, { formatApiError } from "@/lib/api";

const NAV = [
  { key: "overview", label: "Overview", icon: LayoutDashboard },
  { key: "businesses", label: "Businesses", icon: Building2 },
  { key: "users", label: "Users", icon: Users },
];

export default function PlatformPortal() {
  const [active, setActive] = useState("overview");
  const [data, setData] = useState({ overview: null, businesses: [], users: [] });
  const load = () => {
    api.get("/platform/overview").then((r) => setData((d) => ({ ...d, overview: r.data }))).catch(() => {});
    api.get("/platform/businesses").then((r) => setData((d) => ({ ...d, businesses: r.data }))).catch(() => {});
    api.get("/platform/users").then((r) => setData((d) => ({ ...d, users: r.data }))).catch(() => {});
  };
  useEffect(load, []);

  return (
    <Shell title="Platform Admin" subtitle="Everything across BeetDrop" nav={NAV} active={active} onNav={setActive}>
      {active === "overview" && <Overview o={data.overview} />}
      {active === "businesses" && <Businesses rows={data.businesses} />}
      {active === "users" && <UsersTab rows={data.users} reload={load} />}
    </Shell>
  );
}

function Stat({ label, value, sub }) {
  return (
    <Panel className="p-5">
      <div className="text-xs font-semibold uppercase tracking-wider text-purple-300/60">{label}</div>
      <div className="font-head text-3xl font-extrabold text-white mt-1">{value}</div>
      {sub && <div className="text-xs text-purple-400 mt-1">{sub}</div>}
    </Panel>
  );
}

function Overview({ o }) {
  if (!o) return <Panel className="p-12 text-center text-purple-400/70">Loading…</Panel>;
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat label="Businesses" value={o.totals.businesses} />
        <Stat label="Users" value={o.totals.users} />
        <Stat label="Bookings" value={o.totals.bookings} />
        <Stat label="Complaints" value={o.totals.complaints} />
      </div>
      <div className="grid md:grid-cols-3 gap-4">
        <Panel className="p-5">
          <div className="text-sm font-bold text-white mb-3">Subscriptions by plan</div>
          {o.plans.map((p) => <div key={p.plan} className="flex justify-between text-sm text-purple-200/80 py-1 capitalize"><span>{p.plan}</span><span className="text-white font-semibold">{p.count}</span></div>)}
        </Panel>
        <Panel className="p-5">
          <div className="text-sm font-bold text-white mb-3">Subscription status</div>
          {o.subscription_status.map((s) => <div key={s.status} className="flex justify-between text-sm text-purple-200/80 py-1 capitalize"><span>{s.status}</span><span className="text-white font-semibold">{s.count}</span></div>)}
        </Panel>
        <Panel className="p-5">
          <div className="text-sm font-bold text-white mb-3">Users by role</div>
          {o.roles.map((r) => <div key={r.role} className="flex justify-between text-sm text-purple-200/80 py-1 capitalize"><span>{r.role}</span><span className="text-white font-semibold">{r.count}</span></div>)}
        </Panel>
      </div>
    </div>
  );
}

function Businesses({ rows }) {
  if (!rows.length) return <Panel className="p-12 text-center text-purple-400/70">No businesses yet.</Panel>;
  return (
    <div className="overflow-x-auto rounded-xl border border-[#2E1256] bg-[#14052E]">
      <table className="w-full text-sm">
        <thead><tr className="text-left text-xs uppercase tracking-wider text-purple-300 bg-[#1F0842]">{["Business", "Owner", "Plan", "Status", "Verified", "Locations", "Users", "Bookings", "Complaints"].map((h) => <th key={h} className="p-3">{h}</th>)}</tr></thead>
        <tbody>
          {rows.map((b) => (
            <tr key={b.id} className="border-t border-[#230948] hover:bg-[#1E0A3E]" data-testid={`pbiz-${b.id}`}>
              <td className="p-3 text-purple-100 font-medium">{b.name}</td>
              <td className="p-3 text-purple-300">{b.owner_email || "—"}</td>
              <td className="p-3 capitalize text-purple-200">{b.plan || "—"}</td>
              <td className="p-3"><StatusBadge status={b.subscription_status === "active" ? "approved" : "pending"} /></td>
              <td className="p-3">{b.verified ? <span className="text-emerald-400">Yes</span> : <span className="text-amber-300">No</span>}</td>
              <td className="p-3 text-purple-300">{b.locations}</td>
              <td className="p-3 text-purple-300">{b.users}</td>
              <td className="p-3 text-purple-300">{b.bookings}</td>
              <td className="p-3 text-purple-300">{b.complaints}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function UsersTab({ rows, reload }) {
  const [q, setQ] = useState("");
  const [resetId, setResetId] = useState(null);
  const [pw, setPw] = useState("");
  const filtered = rows.filter((u) => `${u.name} ${u.email} ${u.business_name} ${u.role}`.toLowerCase().includes(q.toLowerCase()));
  const toggle = async (u) => {
    try { await api.patch(`/platform/users/${u.id}/status`, { active: u.active === false }); toast.success(`${u.name} updated`); reload(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const doReset = async (u) => {
    if (pw.length < 6) return toast.error("At least 6 characters");
    try { await api.post(`/platform/users/${u.id}/reset-password`, { password: pw }); toast.success(`Password reset for ${u.name}`); setResetId(null); setPw(""); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  return (
    <div className="space-y-4">
      <div className="max-w-sm"><Input data-testid="pusers-search" placeholder="Search users…" value={q} onChange={(e) => setQ(e.target.value)} /></div>
      <div className="overflow-x-auto rounded-xl border border-[#2E1256] bg-[#14052E]">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-xs uppercase tracking-wider text-purple-300 bg-[#1F0842]">{["Name", "Email", "Role", "Business", "Status", "Actions"].map((h) => <th key={h} className="p-3">{h}</th>)}</tr></thead>
          <tbody>
            {filtered.map((u) => (
              <tr key={u.id} className="border-t border-[#230948] hover:bg-[#1E0A3E]" data-testid={`puser-${u.id}`}>
                <td className="p-3 text-purple-100">{u.name}{u.active === false && <span className="ml-2 text-[10px] px-2 py-0.5 rounded-full bg-rose-500/15 text-rose-300 border border-rose-500/30">Inactive</span>}</td>
                <td className="p-3 text-purple-300">{u.email}</td>
                <td className="p-3 capitalize text-purple-200">{u.role}</td>
                <td className="p-3 text-purple-300">{u.business_name}</td>
                <td className="p-3">{u.active === false ? <span className="text-rose-300 text-xs">Inactive</span> : <span className="text-emerald-400 text-xs">Active</span>}</td>
                <td className="p-3">
                  {u.role !== "superadmin" ? (
                    <div className="flex flex-wrap gap-1.5 items-center">
                      <Btn variant="outline" onClick={() => toggle(u)} data-testid={`ptoggle-${u.id}`}>{u.active === false ? "Reactivate" : "Deactivate"}</Btn>
                      <Btn variant="outline" onClick={() => { setResetId(resetId === u.id ? null : u.id); setPw(""); }} data-testid={`preset-${u.id}`}>Reset password</Btn>
                      {resetId === u.id && (
                        <span className="flex items-center gap-1.5">
                          <div className="w-44"><Input data-testid={`preset-input-${u.id}`} placeholder="New password" value={pw} onChange={(e) => setPw(e.target.value)} /></div>
                          <Btn onClick={() => doReset(u)} data-testid={`preset-save-${u.id}`}>Save</Btn>
                        </span>
                      )}
                    </div>
                  ) : <span className="text-xs text-purple-500">Platform</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
